import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Pagination,
  Popconfirm,
  Popover,
  Select,
  Table,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CopyOutlined,
  DownloadOutlined,
  EditOutlined,
  FileTextOutlined,
  HolderOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import CopyToClipboard from "react-copy-to-clipboard";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import AccountManagementCustomerService from "../../services/AccountManagementCustomerService";
import PlTableDualScroll from "./PlTableDualScroll";
import PnlFilterBar from "./PnlFilterBar";
import {
  loadPnlColumnOrder,
  reorderPnlColumns,
  savePnlColumnOrder,
} from "./pnlColumnOrder";
import { currentMonthRange, formatPeriodLabel } from "./pnlFilterUtils";
import {
  ACCOUNT_MANAGEMENT_ORDER_STATUS_LABELS,
  ACCOUNT_MANAGEMENT_ORDER_STATUSES,
  AccountManagementOrderStatusValue,
  IAccountManagementOrder,
  IAccountManagementProfitLoss,
  IAccountManagementProfitLossFilter,
  IAccountManagementProfitLossSummary,
  IAccountManagementOrderUpdate,
  REFUND_OR_CANCEL_ORDER_STATUSES,
} from "../../types/AccountManagementCustomer";
import { can, formatMoney, openNotification } from "../../utils";

const PLACEHOLDER = "—";

const money = (value?: number | null) =>
  value == null ? PLACEHOLDER : formatMoney(value);

const formatDay = (value?: string) => {
  if (!value) return PLACEHOLDER;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : value;
};

const formatDayTime = (value?: string) => {
  if (!value) return PLACEHOLDER;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : value;
};

const localAmount = (value?: number | null, currency?: string) => {
  if (value == null) return PLACEHOLDER;
  const formatted = value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${formatted} ${currency}` : formatted;
};

const signClass = (value?: number | null) => {
  if (value == null) return " acmd-money--muted";
  if (value > 0) return " acmd-money--pos";
  if (value < 0) return " acmd-money--neg";
  return "";
};

const orderStatusLabel = (status?: string) => {
  if (!status) return PLACEHOLDER;
  return (
    ACCOUNT_MANAGEMENT_ORDER_STATUS_LABELS[status as AccountManagementOrderStatusValue] ?? status
  );
};

const orderStatusBadgeClass = (status?: string) => {
  if (!status) return "acmd-flag";
  if (REFUND_OR_CANCEL_ORDER_STATUSES.includes(status as AccountManagementOrderStatusValue)) {
    return "acmd-flag acmd-flag--danger";
  }
  if (["COMPLETED", "SHIPPED", "IN_TRANSIT", "FBA_ORDER"].includes(status)) {
    return "acmd-flag acmd-flag--success";
  }
  return "acmd-flag acmd-flag--pending";
};

const shipmentBadgeClass = (status?: string) => {
  if (!status) return "acmd-flag acmd-flag--muted";
  if (status.toUpperCase() === "DELIVERED") return "acmd-flag acmd-flag--success";
  if (["SHIPPED", "IN_TRANSIT"].includes(status.toUpperCase())) return "acmd-flag acmd-flag--info";
  return "acmd-flag";
};

const isRefundOrCancelStatus = (status?: string | null) =>
  status != null &&
  REFUND_OR_CANCEL_ORDER_STATUSES.includes(status as AccountManagementOrderStatusValue);

const CopyIconButton = ({ text, title = "Panoya kopyala" }: { text: string; title?: string }) => (
  <CopyToClipboard
    text={text}
    onCopy={() => openNotification("success", "Copied to clipboard", text)}
  >
    <button
      type="button"
      className="acmd-tracking-card__copy"
      title={title}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <CopyOutlined />
    </button>
  </CopyToClipboard>
);

const DraggableColumnTitle = ({ label }: { label: string }) => (
  <span className="acmd-pl-col-header">
    <HolderOutlined className="acmd-pl-col-drag" aria-hidden />
    {label}
  </span>
);

const PlOrderIdentityCell = ({
  row,
  canEdit,
  onSaveOrderNote,
  onSaveWarehouseOrderNo,
}: {
  row: IAccountManagementOrder;
  canEdit: boolean;
  onSaveOrderNote: (value: string | null) => Promise<void>;
  onSaveWarehouseOrderNo: (value: string | null) => Promise<void>;
}) => (
  <div className="acmd-pl-order-card">
    <Tooltip title={formatDayTime(row.purchaseDate)}>
      <span className="acmd-pl-order-date">{formatDay(row.purchaseDate)}</span>
    </Tooltip>
    <div className="acmd-pl-order-row">
      <Tooltip title={row.sellerOrderId}>
        <span className="acmd-pl-order-id">{row.sellerOrderId}</span>
      </Tooltip>
      <div className="acmd-pl-order-actions">
        {row.sellerOrderId ? (
          <CopyIconButton text={row.sellerOrderId} title="Sipariş no kopyala" />
        ) : null}
      </div>
    </div>
    <div className="acmd-pl-order-row">
      <span className="acmd-pl-asin-line">
        <span className="acmd-pl-asin-prefix">ASIN</span>
        <span className="acmd-pl-asin">{row.asin}</span>
      </span>
      <div className="acmd-pl-order-actions acmd-pl-order-actions--asin">
        {row.asin ? <CopyIconButton text={row.asin} title="ASIN kopyala" /> : null}
        {(canEdit || row.orderNote) && (
          <NotePopover
            title="Sipariş Notu"
            value={row.orderNote}
            editable={canEdit}
            onSave={onSaveOrderNote}
          >
            <FileTextOutlined
              className={`acmd-pl-note-icon${
                !row.orderNote && canEdit ? " acmd-pl-note-icon--add" : ""
              }`}
            />
          </NotePopover>
        )}
      </div>
    </div>
    <div className="acmd-pl-order-row acmd-pl-order-row--wh">
      <span className="acmd-pl-wh-line">
        <span className="acmd-pl-asin-prefix">WH</span>
        <EditableTextCell
          editable={canEdit}
          editValue={row.warehouseOrderNo ?? null}
          display={row.warehouseOrderNo || PLACEHOLDER}
          displayClassName={row.warehouseOrderNo ? "acmd-pl-wh-value" : "acmd-money--muted acmd-pl-wh-value"}
          onSave={onSaveWarehouseOrderNo}
        />
      </span>
      <div className="acmd-pl-order-actions">
        {row.warehouseOrderNo ? (
          <CopyIconButton text={row.warehouseOrderNo} title="Shipment no kopyala" />
        ) : null}
      </div>
    </div>
  </div>
);

const PlFinanceStackCell = ({
  row,
  canEdit,
  onSaveEarning,
  onSaveTax,
  onSaveWarehouse,
  onSaveProduct,
}: {
  row: IAccountManagementOrder;
  canEdit: boolean;
  onSaveEarning: (value: number | null) => Promise<void>;
  onSaveTax: (value: number | null) => Promise<void>;
  onSaveWarehouse: (value: number | null) => Promise<void>;
  onSaveProduct: (value: number | null) => Promise<void>;
}) => (
  <div className="acmd-pl-finance-stack">
    <div className="acmd-pl-finance-row">
      <span className="acmd-pl-finance-label">Hakediş</span>
      <EditableNumberCell
        editable={canEdit}
        editValue={row.sellerEarningLocal ?? null}
        display={localAmount(row.sellerEarningLocal, row.currencyCode)}
        displayClassName={`acmd-pl-finance-value${
          row.sellerEarningLocal == null ? " acmd-money--muted" : ""
        }`}
        inputClassName="acmd-pl-finance-input"
        tooltip={
          row.sellerEarningLocal == null && row.estimatedProfitUsd != null
            ? `API tahmini kâr: ${formatMoney(row.estimatedProfitUsd)}`
            : undefined
        }
        onSave={onSaveEarning}
      />
    </div>
    <div className="acmd-pl-finance-row">
      <span className="acmd-pl-finance-label">Ciro</span>
      <Tooltip
        title={row.fxRate ? `Kur ${row.fxRate} (${formatDay(row.fxRateDate)})` : "Kur bulunamadı"}
      >
        <span
          className={`acmd-pl-finance-value acmd-money${
            row.revenueUsd == null ? " acmd-money--muted" : ""
          }`}
        >
          {money(row.revenueUsd)}
        </span>
      </Tooltip>
    </div>
    <div className="acmd-pl-finance-row">
      <span className="acmd-pl-finance-label">Vergi</span>
      <EditableNumberCell
        editable={canEdit}
        editValue={row.taxUsdOverride ?? row.effectiveTaxUsd ?? null}
        display={money(row.effectiveTaxUsd)}
        displayClassName={`acmd-pl-finance-value acmd-money${
          row.effectiveTaxUsd == null ? " acmd-money--muted" : ""
        }${row.taxUsdOverride != null ? " acmd-money--manual" : ""}`}
        inputClassName="acmd-pl-finance-input"
        tooltip={`API: ${money(row.taxUsd)}`}
        onSave={onSaveTax}
      />
    </div>
    <div className="acmd-pl-finance-row">
      <span className="acmd-pl-finance-label">Ara Depo</span>
      <EditableNumberCell
        editable={canEdit}
        editValue={row.shippingCostUsdOverride ?? row.warehouseCostUsd ?? null}
        display={money(row.warehouseCostUsd)}
        displayClassName={`acmd-pl-finance-value acmd-money${
          row.warehouseCostUsd == null ? " acmd-money--muted" : ""
        }${row.shippingCostUsdOverride != null ? " acmd-money--manual" : ""}`}
        inputClassName="acmd-pl-finance-input"
        tooltip={`API: ${money(row.shippingFeeUsd)}`}
        onSave={onSaveWarehouse}
      />
    </div>
    <div className="acmd-pl-finance-row">
      <span className="acmd-pl-finance-label">Ürün Mal.</span>
      <EditableNumberCell
        editable={canEdit}
        editValue={row.productCostUsdOverride ?? row.productCostUsd ?? null}
        display={money(row.productCostUsd)}
        displayClassName={`acmd-pl-finance-value acmd-money${
          row.productCostUsd == null ? " acmd-money--muted" : ""
        }${row.productCostUsdOverride != null ? " acmd-money--manual" : ""}`}
        inputClassName="acmd-pl-finance-input"
        tooltip={`API: ${money(row.orderCostUsd)}`}
        onSave={onSaveProduct}
      />
    </div>
  </div>
);

const EditableTextCell = ({
  editable,
  editValue,
  display,
  displayClassName = "",
  tooltip,
  inputClassName = "acmd-pl-text-input",
  multiline = false,
  onSave,
}: {
  editable: boolean;
  editValue: string | null;
  display: ReactNode;
  displayClassName?: string;
  tooltip?: ReactNode;
  inputClassName?: string;
  multiline?: boolean;
  onSave: (value: string | null) => Promise<void>;
}) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(editValue ?? "");
  const [saving, setSaving] = useState(false);
  const committed = useRef(false);

  useEffect(() => {
    setValue(editValue ?? "");
  }, [editValue]);

  const commit = async () => {
    if (committed.current) return;
    committed.current = true;
    const trimmed = value.trim();
    const next = trimmed === "" ? null : trimmed;
    const prev = editValue?.trim() || null;
    if (next === prev) {
      setEditing(false);
      committed.current = false;
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      setEditing(false);
    } finally {
      setSaving(false);
      committed.current = false;
    }
  };

  const startEditing = () => {
    setValue(editValue ?? "");
    setEditing(true);
  };

  if (!editing) {
    return (
      <Tooltip title={tooltip}>
        <span
          className={`${displayClassName}${editable ? " acmd-inline-editable" : ""}`}
          onClick={editable ? startEditing : undefined}
        >
          {display}
        </span>
      </Tooltip>
    );
  }

  if (multiline) {
    return (
      <Input.TextArea
        autoFocus
        rows={3}
        size="small"
        className={inputClassName}
        value={value}
        disabled={saving}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            committed.current = true;
            setValue(editValue ?? "");
            setEditing(false);
            committed.current = false;
          }
        }}
      />
    );
  }

  return (
    <Input
      autoFocus
      size="small"
      className={inputClassName}
      value={value}
      disabled={saving}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onPressEnter={commit}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          committed.current = true;
          setValue(editValue ?? "");
          setEditing(false);
          committed.current = false;
        }
      }}
    />
  );
};

const EditableNumberCell = ({
  editable,
  editValue,
  display,
  displayClassName = "",
  tooltip,
  inputClassName = "acmd-pl-earning-input",
  onSave,
}: {
  editable: boolean;
  editValue: number | null;
  display: ReactNode;
  displayClassName?: string;
  tooltip?: ReactNode;
  inputClassName?: string;
  onSave: (value: number | null) => Promise<void>;
}) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<number | null>(editValue);
  const [saving, setSaving] = useState(false);
  const committed = useRef(false);

  useEffect(() => {
    setValue(editValue);
  }, [editValue]);

  const commit = async () => {
    if (committed.current) return;
    committed.current = true;
    if (value === editValue) {
      setEditing(false);
      committed.current = false;
      return;
    }
    setSaving(true);
    try {
      await onSave(value);
      setEditing(false);
    } finally {
      setSaving(false);
      committed.current = false;
    }
  };

  const startEditing = () => {
    setValue(editValue);
    setEditing(true);
  };

  if (!editing) {
    return (
      <Tooltip title={tooltip}>
        <span
          className={`${displayClassName}${editable ? " acmd-inline-editable" : ""}`}
          onClick={editable ? startEditing : undefined}
        >
          {display}
        </span>
      </Tooltip>
    );
  }

  return (
    <InputNumber
      autoFocus
      size="small"
      className={inputClassName}
      value={value}
      disabled={saving}
      controls={false}
      onChange={(next) => setValue(next as number | null)}
      onBlur={commit}
      onPressEnter={commit}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          committed.current = true;
          setValue(editValue);
          setEditing(false);
          committed.current = false;
        }
      }}
    />
  );
};

const EditableOrderStatusCell = ({
  order,
  editable,
  onSave,
}: {
  order: IAccountManagementOrder;
  editable: boolean;
  onSave: (status: string | null) => Promise<void>;
}) => {
  const [saving, setSaving] = useState(false);

  if (!editable) {
    return order.orderStatus ? (
      <span className={orderStatusBadgeClass(order.orderStatus)}>
        {orderStatusLabel(order.orderStatus)}
      </span>
    ) : (
      <span className="acmd-money--muted">{PLACEHOLDER}</span>
    );
  }

  const wrapClass = order.orderStatus
    ? `acmd-pl-status-wrap ${orderStatusBadgeClass(order.orderStatus)}`
    : "acmd-pl-status-wrap acmd-pl-status-wrap--empty";

  return (
    <div className={wrapClass}>
      <Select
        size="small"
        allowClear
        className="acmd-pl-status-select acmd-pl-status-select--inline"
        placeholder="Durum"
        disabled={saving}
        value={order.orderStatus ?? undefined}
        options={ACCOUNT_MANAGEMENT_ORDER_STATUSES.map((s) => ({
          value: s,
          label: ACCOUNT_MANAGEMENT_ORDER_STATUS_LABELS[s],
        }))}
        onChange={async (next) => {
          const normalized = next ?? null;
          if (normalized === (order.orderStatus ?? null)) {
            return;
          }
          setSaving(true);
          try {
            await onSave(normalized);
          } finally {
            setSaving(false);
          }
        }}
      />
    </div>
  );
};

const NotePopover = ({
  title,
  value,
  editable,
  onSave,
  children,
}: {
  title: string;
  value?: string | null;
  editable: boolean;
  onSave: (value: string | null) => Promise<void>;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  if (!editable && !value) {
    return null;
  }

  const content = editable ? (
    <div className="acmd-pl-note-popover">
      <Input.TextArea
        rows={2}
        value={draft}
        placeholder="Not ekle…"
        onChange={(e) => setDraft(e.target.value)}
      />
      <Button
        size="small"
        type="primary"
        loading={saving}
        onClick={async () => {
          setSaving(true);
          try {
            await onSave(draft.trim() || null);
            setOpen(false);
          } finally {
            setSaving(false);
          }
        }}
      >
        Kaydet
      </Button>
    </div>
  ) : (
    <div className="acmd-pl-note-readonly">{value}</div>
  );

  return (
    <Popover
      title={title}
      content={content}
      open={open}
      onOpenChange={setOpen}
      trigger={editable ? "click" : "hover"}
    >
      {children}
    </Popover>
  );
};

const ProfitAndLoss = ({
  customerId,
  customerName,
}: {
  customerId: number;
  customerName?: string;
}) => {
  const [data, setData] = useState<IAccountManagementProfitLoss | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [editingOrder, setEditingOrder] = useState<IAccountManagementOrder | null>(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState<IAccountManagementProfitLossFilter>(() => {
    const month = currentMonthRange();
    return { purchaseDateFrom: month.from, purchaseDateTo: month.to };
  });
  const [form] = Form.useForm();

  const canEdit = can("account-management-customers:orders:update");
  const canSync = can("account-management-customers:orders:sync");

  const authUserId = localStorage.getItem("id");
  const [columnOrderKeys, setColumnOrderKeys] = useState<string[]>(() =>
    loadPnlColumnOrder(authUserId)
  );
  const dragColumnKeyRef = useRef<string | null>(null);
  const [draggingColumnKey, setDraggingColumnKey] = useState<string | null>(null);
  const [dropTargetColumnKey, setDropTargetColumnKey] = useState<string | null>(null);

  const clearColumnDragState = useCallback(() => {
    dragColumnKeyRef.current = null;
    setDraggingColumnKey(null);
    setDropTargetColumnKey(null);
  }, []);

  const applyColumnReorder = useCallback(
    (fromKey: string, toKey: string) => {
      setColumnOrderKeys((prev) => {
        const next = reorderPnlColumns(prev, fromKey, toKey);
        savePnlColumnOrder(authUserId, next);
        return next;
      });
    },
    [authUserId]
  );

  const pnlHeaderCellProps = useCallback(
    (columnKey: string) => {
      const headerClass = [
        columnKey !== "actions" ? "acmd-pl-th-draggable" : "",
        draggingColumnKey === columnKey ? "acmd-pl-th--dragging" : "",
        dropTargetColumnKey === columnKey && draggingColumnKey !== columnKey
          ? "acmd-pl-th--drop-target"
          : "",
      ]
        .filter(Boolean)
        .join(" ");

      return {
        draggable: columnKey !== "actions",
        className: headerClass || undefined,
        onDragStart: (e: React.DragEvent) => {
          dragColumnKeyRef.current = columnKey;
          setDraggingColumnKey(columnKey);
          setDropTargetColumnKey(null);
          e.dataTransfer.effectAllowed = "move";
        },
        onDragEnter: (e: React.DragEvent) => {
          e.preventDefault();
          if (columnKey !== "actions" && columnKey !== dragColumnKeyRef.current) {
            setDropTargetColumnKey(columnKey);
          }
        },
        onDragOver: (e: React.DragEvent) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
        },
        onDragLeave: () => {
          setDropTargetColumnKey((prev) => (prev === columnKey ? null : prev));
        },
        onDragEnd: () => {
          clearColumnDragState();
        },
        onDrop: (e: React.DragEvent) => {
          e.preventDefault();
          const from = dragColumnKeyRef.current;
          if (from) {
            applyColumnReorder(from, columnKey);
          }
          clearColumnDragState();
        },
      };
    },
    [applyColumnReorder, clearColumnDragState, draggingColumnKey, dropTargetColumnKey]
  );

  const loadData = useCallback(() => {
    setLoading(true);
    AccountManagementCustomerService.getProfitLoss(customerId, filter)
      .then((response) => setData(response.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [customerId, filter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const applyUpdateResponse = (
    order: IAccountManagementOrder,
    summary: IAccountManagementProfitLossSummary
  ) => {
    setData((current) =>
      current
        ? {
            ...current,
            orders: current.orders.map((row) => (row.id === order.id ? order : row)),
            summary,
          }
        : current
    );
  };

  const buildUpdatePayload = (
    order: IAccountManagementOrder,
    overrides: Partial<IAccountManagementOrderUpdate> = {}
  ): IAccountManagementOrderUpdate => ({
    sellerEarningLocal:
      overrides.sellerEarningLocal !== undefined
        ? overrides.sellerEarningLocal
        : order.sellerEarningLocal ?? null,
    taxUsdOverride: overrides.taxUsdOverride ?? order.taxUsdOverride ?? null,
    shippingCostUsdOverride:
      overrides.shippingCostUsdOverride ?? order.shippingCostUsdOverride ?? null,
    productCostUsdOverride:
      overrides.productCostUsdOverride ?? order.productCostUsdOverride ?? null,
    orderStatus: overrides.orderStatus ?? order.orderStatus ?? null,
    orderNote: overrides.orderNote ?? order.orderNote ?? null,
    shippingNote: overrides.shippingNote ?? order.shippingNote ?? null,
    warehouseOrderNo: overrides.warehouseOrderNo ?? order.warehouseOrderNo ?? null,
    shippingAddress: overrides.shippingAddress ?? order.shippingAddress ?? null,
    clearFinancialsOnStatusChange: overrides.clearFinancialsOnStatusChange,
  });

  const saveOrder = async (order: IAccountManagementOrder, payload: IAccountManagementOrderUpdate) => {
    const response = await AccountManagementCustomerService.updateOrder(order.id, payload, filter);
    applyUpdateResponse(response.data.order, response.data.summary);
    return response.data.order;
  };

  const saveField = async (
    order: IAccountManagementOrder,
    overrides: Partial<IAccountManagementOrderUpdate>,
    errorMessage: string
  ) => {
    try {
      await saveOrder(order, buildUpdatePayload(order, overrides));
    } catch (e) {
      console.error(e);
      openNotification("error", "Kaydedilemedi", errorMessage);
    }
  };

  const saveEarning = (order: IAccountManagementOrder, value: number | null) =>
    saveField(order, { sellerEarningLocal: value }, "Hakediş güncellenemedi");

  const saveOrderStatus = (order: IAccountManagementOrder, newStatus: string | null): Promise<void> => {
    const needsRefundConfirm =
      isRefundOrCancelStatus(newStatus) && !isRefundOrCancelStatus(order.orderStatus);

    if (needsRefundConfirm) {
      return new Promise((resolve) => {
        Modal.confirm({
          title: "İade / İptal Onayı",
          content:
            "Bu duruma geçildiğinde hakediş, ciro ve ürün maliyeti silinecek. Yalnızca ara depo maliyeti kalacak ve kar negatif yansıyacaktır. Devam etmek istiyor musunuz?",
          okText: "Evet, uygula",
          cancelText: "Vazgeç",
          okButtonProps: { danger: true },
          onOk: async () => {
            await saveField(
              order,
              { orderStatus: newStatus, clearFinancialsOnStatusChange: true },
              "Sipariş durumu güncellenemedi"
            );
            resolve();
          },
          onCancel: () => resolve(),
        });
      });
    }

    return saveField(order, { orderStatus: newStatus }, "Sipariş durumu güncellenemedi");
  };

  const openEditor = (order: IAccountManagementOrder) => {
    setEditingOrder(order);
    form.setFieldsValue({
      sellerEarningLocal: order.sellerEarningLocal ?? null,
      taxUsdOverride: order.taxUsdOverride ?? null,
      shippingCostUsdOverride: order.shippingCostUsdOverride ?? null,
      productCostUsdOverride: order.productCostUsdOverride ?? null,
      orderStatus: order.orderStatus ?? undefined,
      orderNote: order.orderNote ?? null,
      shippingNote: order.shippingNote ?? null,
    });
  };

  const performEditorSave = async (clearFinancialsOnStatusChange = false) => {
    if (!editingOrder) return;
    const values = await form.validateFields();
    setSaving(true);
    try {
      await saveOrder(
        editingOrder,
        buildUpdatePayload(editingOrder, {
          ...values,
          clearFinancialsOnStatusChange,
        })
      );
      setEditingOrder(null);
      openNotification("success", "Kaydedildi", "Satır güncellendi");
    } catch (e) {
      console.error(e);
      openNotification("error", "Kaydedilemedi", "Satır güncellenemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleEditorSave = async () => {
    if (!editingOrder) return;
    const values = await form.validateFields();
    const newStatus = values.orderStatus as string | undefined;
    const previousStatus = editingOrder.orderStatus;
    const needsRefundConfirm =
      isRefundOrCancelStatus(newStatus) && !isRefundOrCancelStatus(previousStatus);

    if (needsRefundConfirm) {
      Modal.confirm({
        title: "İade / İptal Onayı",
        content:
          "Bu duruma geçildiğinde hakediş, ciro ve ürün maliyeti silinecek. Yalnızca ara depo maliyeti kalacak ve kar negatif yansıyacaktır. Devam etmek istiyor musunuz?",
        okText: "Evet, uygula",
        cancelText: "Vazgeç",
        okButtonProps: { danger: true },
        onOk: () => performEditorSave(true),
      });
      return;
    }
    await performEditorSave(false);
  };

  const handleSync = () => {
    setSyncing(true);
    AccountManagementCustomerService.syncProfitLoss(customerId, filter)
      .then((response) => {
        setData(response.data);
        if (response.data.syncError) {
          openNotification("warning", "Senkronizasyon uyarısı", response.data.syncError);
        } else {
          openNotification(
            "success",
            "Senkronize edildi",
            `${response.data.orders.length} sipariş listelendi`
          );
        }
      })
      .catch(console.error)
      .finally(() => setSyncing(false));
  };

  const orders = useMemo(() => data?.orders ?? [], [data]);
  const summary = data?.summary;

  const [pnlPage, setPnlPage] = useState(1);
  const [pnlPageSize, setPnlPageSize] = useState(20);

  useEffect(() => {
    setPnlPage(1);
  }, [customerId, filter]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(orders.length / pnlPageSize) || 1);
    if (pnlPage > maxPage) {
      setPnlPage(maxPage);
    }
  }, [orders.length, pnlPageSize, pnlPage]);

  const paginatedOrders = useMemo(() => {
    const start = (pnlPage - 1) * pnlPageSize;
    return orders.slice(start, start + pnlPageSize);
  }, [orders, pnlPage, pnlPageSize]);

  const [filterOptions, setFilterOptions] = useState<{
    countries: string[];
    shipmentStatuses: string[];
  }>({ countries: [], shipmentStatuses: [] });

  useEffect(() => {
    if (!orders.length) return;
    setFilterOptions((prev) => ({
      countries: Array.from(
        new Set([
          ...prev.countries,
          ...(orders.map((o) => o.country).filter(Boolean) as string[]),
        ])
      ).sort(),
      shipmentStatuses: Array.from(
        new Set([
          ...prev.shipmentStatuses,
          ...(orders.map((o) => o.shipmentStatus).filter(Boolean) as string[]),
        ])
      ).sort(),
    }));
  }, [orders]);

  const countries = filterOptions.countries;
  const shipmentStatuses = filterOptions.shipmentStatuses;

  const periodLabel = formatPeriodLabel(filter.purchaseDateFrom, filter.purchaseDateTo);

  const downloadExcel = () => {
    const rows = orders.map((order) => ({
      "Sipariş ID": order.sellerOrderId,
      ASIN: order.asin,
      "Shipment No": order.warehouseOrderNo ?? "",
      "Teslimat Adresi": order.shippingAddress ?? "",
      Tarih: formatDay(order.purchaseDate),
      Pazar: order.country ?? "",
      "Para Birimi": order.currencyCode ?? "",
      Hakediş: order.sellerEarningLocal ?? "",
      Ciro: order.revenueUsd ?? "",
      Vergi: order.effectiveTaxUsd ?? "",
      "Ara Depo": order.warehouseCostUsd ?? "",
      "Ürün Mal.": order.productCostUsd ?? "",
      "Toplam Mal.": order.totalCostUsd ?? "",
      Kar: order.profitUsd ?? "",
      "Marj %": order.profitPercent ?? "",
      "Kargo Durumu": order.shipmentStatus ?? "",
      "Takip No": order.trackingNumber ?? "",
      Carrier: order.carrier ?? "",
      "Takip URL": order.trackingUrl ?? "",
      "Sipariş Durumu": orderStatusLabel(order.orderStatus),
      "Sipariş Notu": order.orderNote ?? "",
      "Kargo Notu": order.shippingNote ?? "",
    }));

    if (summary) {
      rows.push({} as (typeof rows)[0]);
      rows.push({
        "Sipariş ID": "ÖZET",
        ASIN: `${summary.orderCount ?? 0} sipariş`,
        Tarih: periodLabel,
        Ciro: summary.revenue ?? "",
        "Ara Depo": summary.warehouseCost ?? "",
        "Ürün Mal.": summary.productCost ?? "",
        "Toplam Mal.": summary.totalCost ?? "",
        Kar: summary.profit ?? "",
        "Marj %": summary.profitPercent ?? "",
      } as (typeof rows)[0]);
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Kar-Zarar");
    const safeName = (customerName ?? `musteri-${customerId}`).replace(/[^\w\-]+/g, "_");
    XLSX.writeFile(workbook, `Kar-Zarar-${safeName}-${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  const columnDefs = useMemo((): Record<string, ColumnsType<IAccountManagementOrder>[number]> => {
    const attachHeader = (
      col: ColumnsType<IAccountManagementOrder>[number],
      label: string
    ): ColumnsType<IAccountManagementOrder>[number] => {
      const key = String(col.key);
      if (key === "actions") {
        return col;
      }
      return {
        ...col,
        title: <DraggableColumnTitle label={label} />,
        onHeaderCell: () => pnlHeaderCellProps(key),
      };
    };

    const defs: Record<string, ColumnsType<IAccountManagementOrder>[number]> = {
      order: attachHeader(
        {
          key: "order",
          width: 224,
          render: (_value, row) => (
            <PlOrderIdentityCell
              row={row}
              canEdit={canEdit}
              onSaveOrderNote={(value) =>
                saveField(row, { orderNote: value }, "Sipariş notu güncellenemedi")
              }
              onSaveWarehouseOrderNo={(value) =>
                saveField(row, { warehouseOrderNo: value }, "Shipment no güncellenemedi")
              }
            />
          ),
        },
        "Sipariş"
      ),
      market: attachHeader(
        {
          key: "market",
      width: 84,
      render: (_value, row) => (
        <div className="acmd-pl-market">
          <span className="acmd-pl-country" title={row.country}>
            {row.country || PLACEHOLDER}
          </span>
          {row.currencyCode && <span className="acmd-type-tag">{row.currencyCode}</span>}
        </div>
      ),
        },
        "Pazar"
      ),
      financeStack: attachHeader(
        {
          key: "financeStack",
          width: 172,
          render: (_value, row) => (
            <PlFinanceStackCell
              row={row}
              canEdit={canEdit}
              onSaveEarning={(value) => saveEarning(row, value)}
              onSaveTax={(value) => saveField(row, { taxUsdOverride: value }, "Vergi güncellenemedi")}
              onSaveWarehouse={(value) =>
                saveField(row, { shippingCostUsdOverride: value }, "Ara depo maliyeti güncellenemedi")
              }
              onSaveProduct={(value) =>
                saveField(row, { productCostUsdOverride: value }, "Ürün maliyeti güncellenemedi")
              }
            />
          ),
        },
        "Finans"
      ),
      totalCostUsd: attachHeader(
        {
          key: "totalCostUsd",
      width: 102,
      align: "right",
      render: (_value, row) => (
        <span className={`acmd-money${row.totalCostUsd == null ? " acmd-money--muted" : ""}`}>
          {money(row.totalCostUsd)}
        </span>
      ),
        },
        "Toplam Mal."
      ),
      profitUsd: attachHeader(
        {
          key: "profitUsd",
      width: 96,
      align: "right",
      render: (_value, row) => (
        <span className={`acmd-money${signClass(row.profitUsd)}`}>{money(row.profitUsd)}</span>
      ),
        },
        "Kar"
      ),
      profitPercent: attachHeader(
        {
          key: "profitPercent",
      width: 74,
      align: "right",
      render: (_value, row) => (
        <span className={`acmd-money${signClass(row.profitPercent)}`}>
          {row.profitPercent == null ? PLACEHOLDER : `${row.profitPercent}%`}
        </span>
      ),
        },
        "Marj"
      ),
      shipmentStatus: attachHeader(
        {
          key: "shipmentStatus",
      width: 118,
      render: (_value, row) => (
        <div className="acmd-pl-shipment">
          {row.shipmentStatus ? (
            <span className={shipmentBadgeClass(row.shipmentStatus)}>{row.shipmentStatus}</span>
          ) : (
            <span className="acmd-money--muted">{PLACEHOLDER}</span>
          )}
          {(canEdit || row.shippingNote) && (
            <NotePopover
              title="Kargo Notu"
              value={row.shippingNote}
              editable={canEdit}
              onSave={(value) =>
                saveField(row, { shippingNote: value }, "Kargo notu güncellenemedi")
              }
            >
              <FileTextOutlined
                className={`acmd-pl-note-icon${
                  !row.shippingNote && canEdit ? " acmd-pl-note-icon--add" : ""
                }`}
              />
            </NotePopover>
          )}
        </div>
      ),
        },
        "Kargo Durumu"
      ),
      trackingNumber: attachHeader(
        {
          key: "trackingNumber",
      width: 148,
      render: (_value, row) => {
        if (!row.trackingNumber && !row.trackingUrl && !row.carrier) {
          return <span className="acmd-money--muted">{PLACEHOLDER}</span>;
        }

        const trackingLabel = row.trackingNumber || "Takip";
        const copyIcon =
          row.trackingNumber != null && row.trackingNumber !== "" ? (
            <CopyIconButton text={row.trackingNumber} title="Takip numarasını kopyala" />
          ) : null;

        const mainContent = (
          <>
            <span className="acmd-tracking-card__number" title={trackingLabel}>
              {trackingLabel}
            </span>
            <span className="acmd-tracking-card__carrier">{row.carrier || PLACEHOLDER}</span>
          </>
        );

        if (row.trackingUrl) {
          return (
            <div className="acmd-tracking-card acmd-tracking-card--link">
              <a
                className="acmd-tracking-card__main"
                href={row.trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {mainContent}
              </a>
              {copyIcon}
            </div>
          );
        }

        return (
          <Tooltip title="Takip URL yok">
            <div className="acmd-tracking-card acmd-tracking-card--static">
              <div className="acmd-tracking-card__main">{mainContent}</div>
              {copyIcon}
            </div>
          </Tooltip>
        );
      },
        },
        "Takip"
      ),
      shippingAddress: attachHeader(
        {
          key: "shippingAddress",
          width: 228,
          onCell: () => ({ className: "acmd-pl-td-address" }),
          render: (_value, row) => (
            <EditableTextCell
              editable={canEdit}
              multiline
              editValue={row.shippingAddress ?? null}
              inputClassName="acmd-pl-text-input acmd-pl-address-input"
              display={
                <span className="acmd-pl-address" title={row.shippingAddress ?? undefined}>
                  {row.shippingAddress || PLACEHOLDER}
                </span>
              }
              displayClassName={row.shippingAddress ? "" : "acmd-money--muted"}
              onSave={(value) =>
                saveField(row, { shippingAddress: value }, "Teslimat adresi güncellenemedi")
              }
            />
          ),
        },
        "Teslimat Adresi"
      ),
      orderStatus: attachHeader(
        {
          key: "orderStatus",
          width: 152,
      render: (_value, row) => (
        <EditableOrderStatusCell
          order={row}
          editable={canEdit}
          onSave={(status) => saveOrderStatus(row, status)}
        />
      ),
        },
        "Sipariş Durumu"
      ),
    };

    if (canEdit) {
      defs.actions = {
        title: "",
        key: "actions",
        width: 48,
        fixed: "right" as const,
        render: (_value: unknown, row: IAccountManagementOrder) => (
          <Tooltip title="Düzenle">
            <EditOutlined className="acmd-note-action" onClick={() => openEditor(row)} />
          </Tooltip>
        ),
      };
    }

    return defs;
  }, [canEdit, pnlHeaderCellProps, draggingColumnKey, dropTargetColumnKey]);

  const columns = useMemo(() => {
    const ordered = columnOrderKeys
      .filter((key) => key !== "actions" && columnDefs[key])
      .map((key) => columnDefs[key]);
    if (canEdit && columnDefs.actions) {
      ordered.push(columnDefs.actions);
    }
    return ordered;
  }, [columnOrderKeys, columnDefs, canEdit]);

  return (
    <div className="acmd-card">
      <div className="acmd-card-head">
        <span className="acmd-card-title">
          Kar / Zarar
          <span className="acmd-card-title-count">{orders.length}</span>
        </span>
        <div className="acmd-pl-head-actions">
          {data?.lastSyncedAt && (
            <span className="acmd-date">Son senkron: {formatDayTime(data.lastSyncedAt)}</span>
          )}
          {orders.length > 0 && (
            <Button size="small" icon={<DownloadOutlined />} onClick={downloadExcel}>
              Excel
            </Button>
          )}
          {canSync && (
            <Popconfirm
              title="Siparişleri senkronize et"
              description="MAS API'den yeni siparişler çekilecek. Shipment no ve adres doluysa ezilmez; boşsa API doldurur. Diğer elle girilen değerlere dokunulmaz."
              okText="Senkronize et"
              cancelText="Vazgeç"
              onConfirm={handleSync}
            >
              <Button size="small" icon={<SyncOutlined />} loading={syncing}>
                Senkronize Et
              </Button>
            </Popconfirm>
          )}
        </div>
      </div>

      <PnlFilterBar
        mode="detail"
        filter={filter}
        onChange={setFilter}
        countries={countries}
        shipmentStatuses={shipmentStatuses}
      />

      {data?.syncError && <div className="acmd-pl-alert">{data.syncError}</div>}

      {summary && orders.length > 0 && (
        <div className="acmd-summary acmd-summary--pl">
          <div className="acmd-pl-period">Özet ({periodLabel})</div>
          <div className="acmd-summary-item">
            <div className="acmd-summary-label">Sipariş</div>
            <div className="acmd-summary-value">{summary.orderCount ?? 0}</div>
          </div>
          <div className="acmd-summary-item">
            <div className="acmd-summary-label">Ciro</div>
            <div className="acmd-summary-value">{money(summary.revenue)}</div>
          </div>
          <div className="acmd-summary-item">
            <div className="acmd-summary-label">Ara Depo</div>
            <div className="acmd-summary-value">{money(summary.warehouseCost)}</div>
          </div>
          <div className="acmd-summary-item">
            <div className="acmd-summary-label">Ürün Mal.</div>
            <div className="acmd-summary-value">{money(summary.productCost)}</div>
          </div>
          <div className="acmd-summary-item">
            <div className="acmd-summary-label">Toplam Mal.</div>
            <div className="acmd-summary-value">{money(summary.totalCost)}</div>
          </div>
          <div className="acmd-summary-item">
            <div className="acmd-summary-label">Kar</div>
            <div className={`acmd-summary-value${signClass(summary.profit)}`}>
              {money(summary.profit)}
            </div>
          </div>
          <div className="acmd-summary-item">
            <div className="acmd-summary-label">Marj</div>
            <div className={`acmd-summary-value${signClass(summary.profitPercent)}`}>
              {summary.profitPercent == null ? PLACEHOLDER : `${summary.profitPercent}%`}
            </div>
          </div>
        </div>
      )}

      <div className="acmd-card-body acmd-card-body--flush">
        <PlTableDualScroll measureKey={`${loading}-${orders.length}-${columns.length}`}>
          <Table
            className="acmd-table acmd-table--pl"
            rowKey="id"
            size="small"
            loading={loading}
            columns={columns}
            dataSource={paginatedOrders}
            tableLayout="fixed"
            scroll={{ x: canEdit ? 1420 : 1372 }}
            pagination={false}
            locale={{
              emptyText: (
                <div className="acmd-empty">
                  Sipariş bulunamadı. Mongerlab User ID tanımlıysa senkronizasyonu çalıştırın.
                </div>
              ),
            }}
          />
        </PlTableDualScroll>
        {orders.length > 0 && (
          <div className="acmd-pl-pagination">
            <Pagination
              size="small"
              current={pnlPage}
              pageSize={pnlPageSize}
              total={orders.length}
              showSizeChanger
              pageSizeOptions={[20, 50, 100]}
              hideOnSinglePage={false}
              showTotal={(total, range) => `${range[0]}-${range[1]} / ${total} sipariş`}
              onChange={(page, pageSize) => {
                setPnlPage(page);
                if (pageSize != null) {
                  setPnlPageSize(pageSize);
                }
              }}
            />
          </div>
        )}
      </div>

      <Modal
        title={editingOrder ? `Sipariş Düzenle — ${editingOrder.sellerOrderId}` : ""}
        open={!!editingOrder}
        onCancel={() => setEditingOrder(null)}
        width={520}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => setEditingOrder(null)}>
            Vazgeç
          </Button>,
          <Button key="save" type="primary" loading={saving} onClick={handleEditorSave}>
            Kaydet
          </Button>,
        ]}
      >
        {editingOrder && (
          <>
            <div className="acmd-info-grid acmd-modal-info">
              <div className="acmd-info-item">
                <div className="acmd-info-label">ASIN</div>
                <div className="acmd-info-value">{editingOrder.asin}</div>
              </div>
              <div className="acmd-info-item">
                <div className="acmd-info-label">Pazar</div>
                <div className="acmd-info-value">
                  {editingOrder.country || PLACEHOLDER}
                  {editingOrder.currencyCode ? ` (${editingOrder.currencyCode})` : ""}
                </div>
              </div>
              <div className="acmd-info-item">
                <div className="acmd-info-label">Satış Fiyatı (API)</div>
                <div className="acmd-info-value">{money(editingOrder.itemPriceUsd)}</div>
              </div>
              <div className="acmd-info-item">
                <div className="acmd-info-label">Kargo Durumu</div>
                <div className="acmd-info-value">{editingOrder.shipmentStatus || PLACEHOLDER}</div>
              </div>
            </div>

            <Form form={form} layout="vertical" className="acmd-modal-form">
              <div className="acmd-form-section">
                <div className="acmd-form-section-title">Finansal</div>
                <Form.Item
                  name="sellerEarningLocal"
                  label={`Hakediş${
                    editingOrder.currencyCode ? ` (${editingOrder.currencyCode})` : " (yerel)"
                  }`}
                  extra="Amazon net ödemesi. Ciro bu tutarın sipariş tarihindeki kurla USD karşılığıdır."
                >
                  <InputNumber style={{ width: "100%" }} controls={false} />
                </Form.Item>
                <Form.Item
                  name="taxUsdOverride"
                  label="Vergi ($)"
                  extra={`Boşsa API: ${money(editingOrder.taxUsd)}`}
                >
                  <InputNumber style={{ width: "100%" }} controls={false} />
                </Form.Item>
                <Form.Item
                  name="shippingCostUsdOverride"
                  label="Ara Depo Maliyeti ($)"
                  extra={`Boşsa API: ${money(editingOrder.shippingFeeUsd)}`}
                >
                  <InputNumber style={{ width: "100%" }} controls={false} />
                </Form.Item>
                <Form.Item
                  name="productCostUsdOverride"
                  label="Ürün Maliyeti ($)"
                  extra={`Boşsa API: ${money(editingOrder.orderCostUsd)}`}
                >
                  <InputNumber style={{ width: "100%" }} controls={false} />
                </Form.Item>
              </div>

              <div className="acmd-form-section">
                <div className="acmd-form-section-title">Durum</div>
                <Form.Item name="orderStatus" label="Sipariş Durumu">
                  <Select
                    allowClear
                    placeholder="Seçin"
                    options={ACCOUNT_MANAGEMENT_ORDER_STATUSES.map((s) => ({
                      value: s,
                      label: ACCOUNT_MANAGEMENT_ORDER_STATUS_LABELS[s],
                    }))}
                  />
                </Form.Item>
              </div>

              <div className="acmd-form-section">
                <div className="acmd-form-section-title">Notlar</div>
                <Form.Item name="orderNote" label="Sipariş Notu">
                  <Input.TextArea rows={2} placeholder="Siparişe özel not…" />
                </Form.Item>
                <Form.Item name="shippingNote" label="Kargo Notu">
                  <Input.TextArea rows={2} />
                </Form.Item>
              </div>
            </Form>
          </>
        )}
      </Modal>
    </div>
  );
};

export default ProfitAndLoss;
